import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { capitalize } from "lodash";

dayjs.extend(utc);

export const LOWEST_PRICE_COLORS_LIST = [
  "#28B178",
  "#F9B426",
  "#FF6E04",
  "#E11818",
  "#004057",
  "#00B2FF",
];

export const isDayOrWeek = (window) => ["day", "week"].includes(window);

export const isMonthOrYear = (window) => ["month", "year"].includes(window);

export const isPlan = (window) => window === "plan";

export const getNow = (performance, siteNow) => {
  let now = "";

  if (performance.length > 0) {
    const ts = performance[0].timestamp;
    const offset = ts.slice(-6);

    const current = dayjs().utcOffset(offset);

    if (siteNow) {
      const [hour, minute] = siteNow.split(":").map(Number);
      now = current
        .hour(hour)
        .minute(minute)
        .second(0)
        .format("YYYY-MM-DDTHH:mm:ssZ");
    } else {
      now = current.format("YYYY-MM-DDTHH:mm:ssZ");
    }
  }

  return now;
};

export const getTickInterval = (window) => {
  if (window === "day") {
    return 20;
  } else if (window === "week") {
    return 30;
  } else {
    return 1;
  }
};

export const getTopYAxisName = (selectedType, window) => {
  if (selectedType.length > 1) {
    return "kW";
  } else if (selectedType.length === 1) {
    const token = selectedType[0];
    switch (token) {
      case "solar":
        if (isMonthOrYear(window)) {
          return "Solar Generating (kWh)";
        } else {
          return "Solar Generating (kW)";
        }
      case "grid":
        if (isMonthOrYear(window)) {
          return `<span>
              <span style="margin-right: 14px">Export (kWh)</span>
              <span>Import (kWh)</span>
            </span>`;
        } else {
          return "Import (kW)";
        }
      case "battery":
        if (isMonthOrYear(window)) {
          return `<span>
              <span style="margin-right: 14px">Charge (kWh)</span>
              <span>Discharge (kWh)</span>
            </span>`;
        } else {
          return "Discharge (kWh)";
        }
      case "soc":
        return "Battery state of charge (%)";
      case "house":
        if (isMonthOrYear(window)) {
          return "House Usage (kWh)";
        } else {
          return "House Usage (kW)";
        }
      case "ev":
        if (isMonthOrYear(window)) {
          return "EV Charge (kWh)";
        } else {
          return "EV Charge (kW)";
        }
      case "hotwater":
        if (isMonthOrYear(window)) {
          return "Hot Water (kWh)";
        } else {
          return "Hot Water (kW)";
        }
      case "cost":
      case "cost_cumulative":
        return "Today's total usage cost ($)";
      default:
        return "Import";
    }
  } else {
    return "Import";
  }
};

export const getPerformanceChartHeight = (
  type = "main",
  window,
  fixedHeight,
  id = "history-chart-container"
) => {
  if (fixedHeight) return fixedHeight;
  const parts = window === "day" ? [4, 3] : [6, 4];
  let height = type === "main" ? 240 : 160;
  const element = document.getElementById(id);
  if (element) {
    height = (element.offsetHeight * (type === "main" ? parts[0] : parts[1])) / 10;
  }
  return height;
};

export const getPlotPosition = (performance, window, siteNow) => {
  let plotPos = undefined;
  const now = getNow(performance, siteNow);
  if (now && isDayOrWeek(window)) {
    plotPos = findTimestampIndex(performance, now);
  }

  return plotPos;
};

export const findClosestPointByX = (chart, xValue) => {
  let closestPoint = null;
  let minDistance = Infinity;

  chart.series.forEach(function (series) {
    if (!series.visible) return;

    series.points.forEach(function (point) {
      var distance = Math.abs(point.x - xValue);
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = point;
      }
    });
  });

  return closestPoint;
};

export const getUnitLocalTime = (dateTime) => {
  const tz = dateTime.slice(-6);
  return dayjs(dateTime).utcOffset(tz);
};

export const getFullDateTimeWOMS = (dateTime, window) => {
  let fs = "ha";
  if (window === "week") {
    fs = "dd";
  } else if (window === "month") {
    fs = "D";
  } else if (window === "year") {
    fs = "MMM";
  }

  return getUnitLocalTime(dateTime).format(fs);
};

export const evaluatePriceCategory = (yVal, selectedType) => {
  if (selectedType === "price_import") {
    if (yVal < 0.3) return "low";
    if (yVal >= 0.3 && yVal <= 0.5) return "average";
    if (yVal > 0.5) return "high";
  } else if (selectedType === "price_export") {
    if (yVal < 0.0) return "high";
    if (yVal >= 0.0 && yVal <= 30.0) return "neutral";
    if (yVal > 30.0) return "low";
  }
  return "neutral";
};

export const hexToRgba = (hex, alpha = 0.4) => {
  const bigint = parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getChartColor = (type, opac = false, alpha = 0.4) => {
  let color = "#F59300";

  switch (type) {
    case "solar":
      color = "#FF6E04";
      break;
    case "grid":
    case "grid_import":
      color = "#004057";
      break;
    case "grid_export":
      return "rgba(0, 64, 87, 0.5)";
    case "battery":
    case "battery_import":
    case "soc":
      color = "#F9B426";
      break;
    case "battery_export":
      return "rgba(249, 180, 38, 0.5)";
    case "house":
      color = "#28B178";
      break;
    case "ev":
      color = "#4EC1EA";
      break;
    case "low":
      color = LOWEST_PRICE_COLORS_LIST[0];
      break;
    case "average":
      color = LOWEST_PRICE_COLORS_LIST[1];
      break;
    case "high":
      color = LOWEST_PRICE_COLORS_LIST[2];
      break;
    case "neutral":
      color = "#004057";
      break;
    case "cost":
    case "cost_cumulative":
      color = "#008080";
      break;
    case "hotwater":
      color = "#E11818";
      break;
    default:
      color = "#F59300";
  }

  return opac ? hexToRgba(color, alpha) : hexToRgba(color, 0.75);
};

export const chartOptions = {
  title: {
    text: "",
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    type: "datetime",
  },
  legend: {
    enabled: false,
  },
  exporting: {
    buttons: {
      contextButton: {
        enabled: false,
      },
    },
  },
  time: {
    useUTC: false,
  },
  loading: {
    style: {
      opacity: 0.8,
    },
  },
};

export const batteryChartOptions = (window) => {
  return {
    title: {
      text: getSocChartTitle(window),
      style: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#004057",
      },
      margin: 0,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: "datetime",
    },
    yAxis: {
      gridLineDashStyle: "longdashdot",
      gridLineColor: "#231F20",
      gridLineWidth: 0.5,
      max: 100,
      title: {
        text: "",
      },
      labels: {
        format: "{value} %",
        style: {
          fontWeight: 600,
          color: "#004057",
        },
      },
    },
    legend: {
      enabled: false,
    },
    exporting: {
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    time: {
      useUTC: false,
    },
    loading: {
      style: {
        opacity: 0.8,
      },
    },
  };
};

export const getSocChartTitle = (window) =>
  ["month", "year"].includes(window)
    ? "Electrobank Utilisation (%)"
    : "Electrobank State of Charge (%)";

export const getTooltipTitle = (dateTime, window, showForecastLabel = false) => {
  const ts = getUnitLocalTime(dateTime);
  let title = "";
  if (window === "day") {
    title = ts.format("h.mma");
  } else if (window === "week") {
    title = ts.format("dddd h.mma");
  } else if (window === "month") {
    title = ts.format("dddd D");
  } else if (window === "year") {
    title = ts.format("MMMM");
  }

  const isForecast = showForecastLabel && ts.diff(new Date()) > 1;

  return title + (isForecast ? " (Forecast)" : "");
};

export const getTooltipUnit = (window) => {
  if (isDayOrWeek(window)) {
    return "kW";
  } else {
    return "kWh";
  }
};

export const generateTooltip = (point, window) => {
  if (!point || !point.point || typeof point.y !== "number") return "";
  const isCost = ["Cost_cumulative", "Buy", "Sell"].includes(point.point.label);
  const isSoc = point.point.label === "Soc";
  let label = point.point.label.replace("_", " ");
  let unit = getTooltipUnit(window);
  let value = isCost ? formatCurrency(point.y) : point.y.toFixed(1);
  if (isSoc) {
    label = "State of charge";
    unit = "%";
  } else if (point.point.label === "Cost_cumulative") {
    label = "Usage cost";
    unit = "";
  }

  return (
    "<br/>" +
    "<span style='color: " +
    point.color +
    "'>" +
    "<b>" +
    label +
    " : " +
    "</b>" +
    "<b>" +
    value +
    unit +
    "</b>" +
    "</span>"
  );
};

export const getPlotOptions = (window, selectedType) => {
  if (
    isMonthOrYear(window) &&
    selectedType.length > 0 &&
    ["grid", "battery"].includes(selectedType[0])
  ) {
    return {
      column: {
        stacking: "normal",
      },
    };
  } else {
    return {
      column: {
        stacking: undefined,
      },
    };
  }
};

export const addToPerformanceChart = (performance, list, key, plotPos) => {
  const data = [];
  performance?.forEach((it) => {
    data.push({
      y: it[key],
      timestamp: it["timestamp"],
      label: capitalize(key),
    });
  });

  list.push({
    data: data,
    color: getChartColor(key),
    lineWidth: 1,
    zoneAxis: "x",
    zones: plotPos && [
      {
        value: plotPos,
        fillColor: getChartColor(key),
      },
      {
        fillColor: getChartColor(key, true),
        dashStyle: "dash",
      },
    ],
  });

  return list;
};

export const getBatterySeries = (performance, plotPos) => {
  return [
    {
      data: performance?.map((it) => {
        return {
          y: it["soc"],
          timestamp: it["timestamp"],
          label: "Soc",
        };
      }),
      color: getChartColor("soc"),
      lineWidth: 1,
      zoneAxis: "x",
      zones: plotPos && [
        {
          value: plotPos,
          fillColor: getChartColor("soc"),
        },
        {
          fillColor: getChartColor("soc", true),
          dashStyle: "dash",
        },
      ],
    },
  ];
};

export const getPriceSeries = (performance, plotPos) => {
  const keys = [
    { key: "price_import", label: "Buy" },
    { key: "price_export", label: "Sell" },
  ];
  const series = [];

  keys.forEach((selectedPriceType) => {
    const item = {
      id: selectedPriceType.key,
      name: selectedPriceType.key,
      data: performance?.map((it, i) => {
        const yVal = it[selectedPriceType.key];
        return {
          y: yVal,
          timestamp: it["timestamp"],
          label: selectedPriceType.label,
          color: getChartColor(
            evaluatePriceCategory(yVal, selectedPriceType.key),
            i >= plotPos
          ),
        };
      }),
      lineWidth: 1,
    };

    series.push(item);
  });
  return series;
};

export const getXCategories = (performance, window, siteNow) => {
  const plotPos = getPlotPosition(performance, window, siteNow);
  const plotBandPos = getFirstDemandRange(performance);

  return {
    xAxis: {
      type: "datetime",
      plotLines: [
        {
          color: "#1b263b",
          width: 2,
          value: plotPos,
          zIndex: 3,
          dashStyle: "dash",
          label: {
            text: "Now",
            rotation: 0,
            y: 20,
            style: {
              color: "#1b263b",
            },
          },
        },
      ],
      plotBands: plotBandPos && [
        {
          color: "#E1181822",
          from: plotBandPos.from,
          to: plotBandPos.to,
        },
      ],
      crosshair: {
        width: 2,
        color: "#191970",
        dashStyle: "shortdot",
        zIndex: 3,
      },
      categories: performance?.map((it) =>
        getFullDateTimeWOMS(it.timestamp, window)
      ),
      labels: {
        style: {
          fontWeight: 600,
          color: "#004057",
        },
        step: 2,
        autoRotation: false,
      },
      showFirstLabel: true,
      showLastLabel: true,
      minTickInterval: getTickInterval(window),
    },
  };
};

export const formatCurrency = (value, roundDollars = false) => {
  if (value > -1 && value < 1) {
    const cents = Math.round(value * 100);
    return `${cents}¢`;
  } else {
    return roundDollars ? `$${Math.round(value)}` : `$${value.toFixed(2)}`;
  }
};

export const getFirstDemandRange = (data) => {
  let from = -1;
  let to = -1;
  let inSequence = false;

  for (let i = 0; i < data.length; i++) {
    const item = data[i];

    if (item.demand_window) {
      if (!inSequence) {
        from = i;
        inSequence = true;
      }
      to = i;
    } else if (inSequence) {
      break;
    }
  }

  return from !== -1 ? { from, to } : null;
};

export const findTimestampIndex = (data, targetDateStr) => {
  if (data.length === 0) return undefined;
  const target = dayjs(targetDateStr);

  for (let i = 0; i < data.length - 1; i++) {
    const current = dayjs(data[i].timestamp);
    const next = dayjs(data[i + 1].timestamp);

    if (
      target.isSame(current) ||
      (target.isAfter(current) && target.isBefore(next))
    ) {
      return i;
    }
  }

  return undefined;
};

export const isInDemandWindow = (index, plotBandPos) => {
  if (plotBandPos) {
    return index >= plotBandPos?.from && index <= plotBandPos?.to;
  }

  return false;
};
